import { z } from "zod";

import { myvModeTypeDto } from "@/types/backoffice/dto/myv/myv-mode-type-dto.ts";
import { myvProgrammingTypeDtoSchema } from "@/types/backoffice/dto/myv/myv-programming-type-dto.ts";

export enum EAutoDiagDiagnosticTestResult {
  OK = "OK",
  NOK_NO_POWER = "NOK_NO_POWER",
  NOK_NO_MODULATION = "NOK_NO_MODULATION",
  NOK_UNREACHABLE = "NOK_UNREACHABLE",
  NOK_DEVICE_UNKNOWN = "NOK_DEVICE_UNKNOWN",
  FAILED_BACKEND = "FAILED_BACKEND",
}

export enum EAutoDiagDeviceInfoEnergyManager {
  HAS_ENERGY_MANAGER = "HAS_ENERGY_MANAGER",
  NO_ENERGY_MANAGER = "NO_ENERGY_MANAGER",
  ERROR_INFRA_VOLTALIS = "ERROR_INFRA_VOLTALIS",
}

export const autoDiagFailuresDtoSchema = z.object({
  failedToFetchMyVEnergyManager: z.boolean(),
});

export const autoDiagDeviceSensorDtoSchema = z.object({
  csId: z.number(),
  hwId: z.number(),
  installationDatetime: z.coerce.date(),
  serial: z.string().nullable(),
});

export const autoDiagDeviceDiagnosticDtoSchema = z.object({
  start: z.coerce.date().nullable(),
  end: z.coerce.date().nullable(),
  result: z.nativeEnum(EAutoDiagDiagnosticTestResult).nullable(),
});

export const autoDiagDeviceInfoDtoSchema = z.object({
  applianceTypeTranslationKey: z.string().nullable(),
  canDiagnose: z.boolean(),
  csLinkId: z.number(),
  hasEnergyManager: z.nativeEnum(EAutoDiagDeviceInfoEnergyManager).nullable(),
  hwId: z.number(),
  measuredPower: z.number().nullable(),
  modulatorType: z.string(),
  myVName: z.string().nullable(),
  roomTypeTranslationKey: z.string().nullable(),
  serial: z.string().nullable(),
  technicalName: z.string().nullable(),
});

export const autoDiagDeviceMemoActionDtoSchema = z.enum(["SYNCHRONIZE_MYV_PROGRAM", "GET_SUPPORT"]);

export const autoDiagDeviceMemoDtoSchema = z.object({
  content: z.string(),
  actions: z.array(autoDiagDeviceMemoActionDtoSchema),
});

export const autoDiagDeviceProgrammingDtoSchema = z.object({
  mode: myvModeTypeDto,
  type: myvProgrammingTypeDtoSchema,
  failedToFetchMyVProgramming: z.boolean(),
  comform: z.enum(["CONFORM", "NOT_CONFORM", "FAILED"]).nullish(),
});

export const autoDiagDeviceDtoSchema = z.object({
  diagnostic: autoDiagDeviceDiagnosticDtoSchema.nullable(),
  info: autoDiagDeviceInfoDtoSchema.nullable(),
  programming: autoDiagDeviceProgrammingDtoSchema.nullable(),
  memo: autoDiagDeviceMemoDtoSchema.nullable(),
  sensor: autoDiagDeviceSensorDtoSchema.nullable(),
});

export const autoDiagDtoSchema = z.object({
  devices: z.array(autoDiagDeviceDtoSchema),
  failures: autoDiagFailuresDtoSchema,
  lastSync: z.coerce.date().nullable(),
});

export type TAutoDiagFailuresDto = z.infer<typeof autoDiagFailuresDtoSchema>;
export type TAutoDiagDeviceSensorDto = z.infer<typeof autoDiagDeviceSensorDtoSchema>;
export type TAutoDiagDeviceDiagnosticDto = z.infer<typeof autoDiagDeviceDiagnosticDtoSchema>;
export type TAutoDiagDeviceInfoDto = z.infer<typeof autoDiagDeviceInfoDtoSchema>;
export type TAutoDiagDeviceMemoActionDto = z.infer<typeof autoDiagDeviceMemoActionDtoSchema>;
export type TAutoDiagDeviceMemoDto = z.infer<typeof autoDiagDeviceMemoDtoSchema>;
export type TAutoDiagDeviceProgrammingDto = z.infer<typeof autoDiagDeviceProgrammingDtoSchema>;
export type TAutoDiagDeviceDto = z.infer<typeof autoDiagDeviceDtoSchema>;
export type TAutoDiagDto = z.infer<typeof autoDiagDtoSchema>;
